import { useState, useEffect, useCallback, useMemo } from 'react';
import { CalendarRange, Plus, Loader2, Trash2, Wand2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import orchardService from '../../services/orchard';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { HarvestWindow, HarvestWindowStatus } from '../../types/orchard';

// When this block is due to be picked, season by season.
//
// The spray module reads these dates: a spray whose withholding period is still
// running when picking opens is held for a Spray Admin decision. A block with no
// window on record cannot be cleared or faulted — the spray form says so rather
// than implying the spray is safe — so the empty state here matters as much as
// the filled one.

interface Props {
  areaId: number;
  canEdit: boolean;
}

const STATUS_LABEL: Record<HarvestWindowStatus, string> = {
  estimate: 'Estimate',
  confirmed: 'Confirmed',
  actual: 'Actual (first pick)',
};

const STATUS_CLASS: Record<HarvestWindowStatus, string> = {
  estimate: 'bg-neutral-100 text-neutral-600',
  confirmed: 'bg-blue-100 text-blue-700',
  actual: 'bg-green-100 text-green-700',
};

const currentYear = new Date().getFullYear();

const emptyForm = {
  season_year: currentYear,
  earliest_harvest_date: '',
  expected_end_date: '',
  status: 'estimate' as HarvestWindowStatus,
  notes: '',
};

export function HarvestWindowsPanel({ areaId, canEdit }: Props) {
  const timezone = useOrgTimezone();
  const [windows, setWindows] = useState<HarvestWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestionNote, setSuggestionNote] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await orchardService.getHarvestWindows(areaId);
      if (res.success && res.data) setWindows(res.data);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to load harvest windows');
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);

  // The window a spray planned today would be checked against — the earliest
  // one that has not finished. Shown first because it is the one that decides
  // what can be sprayed.
  const bindingWindow = useMemo(() => {
    return [...windows]
      .filter((w) => (w.expected_end_date || w.earliest_harvest_date) >= today)
      .sort((a, b) => (a.earliest_harvest_date < b.earliest_harvest_date ? -1 : 1))[0] || null;
  }, [windows, today]);

  const openForm = (existing?: HarvestWindow) => {
    setSuggestionNote(null);
    setError(null);
    setForm(existing
      ? {
          season_year: existing.season_year,
          earliest_harvest_date: existing.earliest_harvest_date.slice(0, 10),
          expected_end_date: existing.expected_end_date?.slice(0, 10) || '',
          status: existing.status,
          notes: existing.notes || '',
        }
      : { ...emptyForm });
    setShowForm(true);
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    setError(null);
    setSuggestionNote(null);
    try {
      const res = await orchardService.suggestHarvestWindow(areaId, form.season_year);
      if (!res.success) {
        setError(res.error?.message || 'Failed to derive dates');
        return;
      }
      const s = res.data;
      if (!s) {
        setSuggestionNote(
          'No maturity records exist for this cultivar yet, so there is nothing to derive dates from. Enter them by hand.'
        );
        return;
      }
      setForm((f) => ({
        ...f,
        earliest_harvest_date: s.earliest_harvest_date || f.earliest_harvest_date,
        expected_end_date: s.expected_end_date || f.expected_end_date,
        status: 'estimate',
      }));
      setSuggestionNote(
        `Shifted from ${s.based_on.cultivar_name ?? 'this cultivar'}'s ${s.based_on.season_year} harvest` +
        `${s.based_on.region_name ? ` in ${s.based_on.region_name}` : ''}.` +
        (s.window_source === 'cultivar'
          ? ' The end date is the cultivar’s usual harvest window, not a recorded last pick.'
          : s.window_source === 'default'
            ? ' No last pick or harvest window is recorded, so the end date assumes a default window.'
            : '') +
        ' Check it against this block.'
      );
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to derive dates');
    } finally {
      setSuggesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.earliest_harvest_date) {
      setError('An earliest harvest date is required');
      return;
    }
    if (form.expected_end_date && form.expected_end_date < form.earliest_harvest_date) {
      setError('Harvest end date cannot be before the earliest harvest date');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await orchardService.saveHarvestWindow(areaId, {
        season_year: form.season_year,
        earliest_harvest_date: form.earliest_harvest_date,
        expected_end_date: form.expected_end_date || null,
        status: form.status,
        notes: form.notes.trim() || null,
      });
      setShowForm(false);
      setForm({ ...emptyForm });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to save harvest window');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (w: HarvestWindow) => {
    setError(null);
    try {
      await orchardService.deleteHarvestWindow(w.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Failed to remove harvest window');
    }
  };

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3 gap-3">
          <div className="flex items-center gap-2">
            <CalendarRange className="h-4 w-4 text-neutral-500" />
            <h3 className="text-sm font-semibold text-neutral-700">Harvest windows</h3>
          </div>
          {canEdit && !showForm && (
            <Button variant="secondary" size="sm" onClick={() => openForm()}>
              <Plus className="h-3.5 w-3.5 mr-1" /> Add season
            </Button>
          )}
        </div>

        <p className="text-xs text-neutral-500 mb-4">
          When this block is due to be picked. Spray planning checks a chemical's withholding
          period against the earliest date here — without one, a spray on this block can be
          neither cleared nor flagged.
        </p>

        {error && (
          <div className="mb-3 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-neutral-500 py-3">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {!windows.length && !showForm && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  No harvest dates recorded. Sprays targeting this block will be reported as
                  unverified against their withholding period.
                </span>
              </div>
            )}

            {windows.length > 0 && (
              <ul className="divide-y divide-neutral-100">
                {windows.map((w) => {
                  const isBinding = bindingWindow?.id === w.id;
                  const past = (w.expected_end_date || w.earliest_harvest_date) < today;
                  return (
                    <li key={w.id} className="py-2.5 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-neutral-800">
                            {w.season_year} season
                          </span>
                          <span className={`text-[11px] px-1.5 py-0.5 rounded ${STATUS_CLASS[w.status]}`}>
                            {STATUS_LABEL[w.status]}
                          </span>
                          {w.source === 'bins' && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700" title="Written from the first and last bin picked">
                              From bins
                            </span>
                          )}
                          {isBinding && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                              Used for spray checks
                            </span>
                          )}
                          {past && (
                            <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
                              Finished
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-neutral-600 mt-0.5">
                          Earliest pick {formatLocalDateInTz(w.earliest_harvest_date, timezone)}
                          {w.expected_end_date && (
                            <> → {formatLocalDateInTz(w.expected_end_date, timezone)}</>
                          )}
                        </p>
                        {w.notes && <p className="text-xs text-neutral-500 mt-0.5">{w.notes}</p>}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openForm(w)}>
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(w)}>
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {showForm && (
              <div className="mt-4 rounded-md border border-neutral-200 p-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Season year
                    </label>
                    <input
                      type="number"
                      min={2000}
                      max={2100}
                      value={form.season_year}
                      onChange={(e) => setForm({ ...form, season_year: Number(e.target.value) })}
                      className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Earliest harvest date
                    </label>
                    <input
                      type="date"
                      value={form.earliest_harvest_date}
                      onChange={(e) => setForm({ ...form, earliest_harvest_date: e.target.value })}
                      className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Expected end <span className="text-neutral-400">(optional)</span>
                    </label>
                    <input
                      type="date"
                      value={form.expected_end_date}
                      onChange={(e) => setForm({ ...form, expected_end_date: e.target.value })}
                      className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Status
                    </label>
                    <select
                      value={form.status}
                      onChange={(e) =>
                        setForm({ ...form, status: e.target.value as HarvestWindowStatus })
                      }
                      className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm"
                    >
                      {(Object.keys(STATUS_LABEL) as HarvestWindowStatus[]).map((s) => (
                        <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Notes <span className="text-neutral-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="e.g. first pick of the early strain on the north rows"
                      className="w-full rounded-md border border-neutral-300 px-2.5 py-1.5 text-sm"
                    />
                  </div>
                </div>

                {suggestionNote && (
                  <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5">
                    {suggestionNote}
                  </p>
                )}

                <div className="flex items-center justify-between gap-2 pt-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSuggest}
                    disabled={suggesting}
                  >
                    {suggesting
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <Wand2 className="h-3.5 w-3.5 mr-1" />}
                    Derive from cultivar maturity
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={saving}>
                      {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                      Save season
                    </Button>
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500">
                  Saving a season that already exists replaces it — revising an estimate is the
                  normal case, not an error.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default HarvestWindowsPanel;
